import React from "react";
import { MoreHorizontal, Inbox, ChevronUp, ChevronDown, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";

export function DataTable({ columns, rows, loading, onRowClick, rowActions, emptyText = "No records found", testId, sortKey, sortDir, onSort,
  selectable, selectedIds, onToggleRow, allSelected, onToggleAll }) {
  const leadCols = (selectable ? 1 : 0);
  return (
    <div className="overflow-hidden rounded-xl border border-white/[0.08] bg-[#141417]">
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm" data-testid={testId}>
          <thead>
            <tr className="border-b border-white/[0.08]">
              {selectable && (
                <th className="w-10 px-4 py-3">
                  <Checkbox checked={allSelected} onCheckedChange={onToggleAll} data-testid={`${testId}-select-all`} aria-label="Select all" />
                </th>
              )}
              {columns.map((c) => {
                const sortable = c.sortable && onSort;
                const active = sortKey === (c.sortField || c.key);
                return (
                  <th
                    key={c.key}
                    className={cn(
                      "whitespace-nowrap px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground",
                      c.align === "right" && "text-right",
                      c.headerClassName,
                    )}
                  >
                    {sortable ? (
                      <button
                        data-testid={`sort-${c.sortField || c.key}`}
                        onClick={() => onSort(c.sortField || c.key)}
                        className={cn("inline-flex items-center gap-1 uppercase tracking-wider transition-colors hover:text-foreground", active && "text-foreground", c.align === "right" && "flex-row-reverse")}
                      >
                        {c.label}
                        {active ? (sortDir === "asc" ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />) : <ChevronsUpDown className="h-3.5 w-3.5 opacity-40" />}
                      </button>
                    ) : c.label}
                  </th>
                );
              })}
              {rowActions && <th className="w-12 px-4 py-3" />}
            </tr>
          </thead>
          <tbody>
            {loading &&
              Array.from({ length: 6 }).map((_, i) => (
                <tr key={i} className="border-b border-white/[0.05]">
                  {selectable && <td className="px-4 py-3" />}
                  {columns.map((c) => (
                    <td key={c.key} className="px-4 py-3">
                      <Skeleton className="h-4 w-24 bg-white/[0.06]" />
                    </td>
                  ))}
                  {rowActions && <td />}
                </tr>
              ))}

            {!loading &&
              rows.map((row, idx) => (
                <tr
                  key={row.id || idx}
                  onClick={() => onRowClick && onRowClick(row)}
                  data-testid={`row-${row.id || idx}`}
                  className={cn(
                    "border-b border-white/[0.05] transition-colors last:border-0",
                    onRowClick && "cursor-pointer hover:bg-white/[0.025]",
                    selectedIds?.has?.(row.id) && "bg-white/[0.04]",
                  )}
                >
                  {selectable && (
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <Checkbox checked={!!selectedIds?.has?.(row.id)} onCheckedChange={() => onToggleRow(row.id)} data-testid={`row-select-${row.id || idx}`} aria-label="Select row" />
                    </td>
                  )}
                  {columns.map((c) => (
                    <td
                      key={c.key}
                      className={cn(
                        "whitespace-nowrap px-4 py-3 text-foreground/90",
                        c.align === "right" && "text-right",
                        c.mono && "font-mono",
                        c.className,
                      )}
                    >
                      {c.render ? c.render(row) : row[c.key] ?? "—"}
                    </td>
                  ))}
                  {rowActions && (
                    <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            data-testid={`row-actions-${row.id || idx}`}
                            className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-white/[0.06] hover:text-foreground"
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="border-white/[0.1] bg-[#1a1a1e]">
                          {rowActions(row).map((a, i) => (
                            <DropdownMenuItem
                              key={i}
                              onClick={a.onClick}
                              data-testid={a.testId}
                              className={cn("cursor-pointer gap-2 text-sm", a.danger && "text-[#EF4444] focus:text-[#EF4444]")}
                            >
                              {a.icon && <a.icon className="h-4 w-4" strokeWidth={1.5} />}
                              {a.label}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  )}
                </tr>
              ))}

            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={columns.length + leadCols + (rowActions ? 1 : 0)} className="py-16 text-center">
                  <div className="flex flex-col items-center gap-2 text-muted-foreground">
                    <Inbox className="h-8 w-8" strokeWidth={1.25} />
                    <span className="text-sm">{emptyText}</span>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
